import type { ReactElement } from 'react';
import { Users, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/Tooltip';
import type { SignalWithRelatedIssuesCount } from '@momentum/types';
import { SignalCategory, SignalInstalledStatus } from '@momentum/types';
import { useLocaleDateString } from '@/shared/hooks/useLocaleDateString';
import { MentionText } from '@/shared/components/mention-text/MentionText';
import { SignalAuthorDisplay } from '../../shared/components/SignalAuthorDisplay';
import { Badge } from '@/shared/components/ui/Badge';
import { SeverityBadge } from './SeverityBadge';
import { SignalToggle } from './SignalToggle';
import { SweepBar } from './SweepBar';

interface KnowledgeSignalCardProps {
  signal: SignalWithRelatedIssuesCount;
  currentUserId: string;
  owner?: {
    fullName: string;
    imageUrl?: string;
  };
  employeeMap: Map<string, { fullName: string; imageUrl?: string }>;
  onSignalClick: (signal: SignalWithRelatedIssuesCount) => void;
  onToggleActive: (
    signal: SignalWithRelatedIssuesCount,
    isActive: boolean
  ) => void;
  isTogglingActive: boolean;
}

export const SignalCard = ({
  signal,
  currentUserId,
  owner,
  employeeMap,
  onSignalClick,
  onToggleActive,
  isTogglingActive,
}: KnowledgeSignalCardProps): ReactElement => {
  const { t } = useTranslation('knowledge');
  const { toLocaleDateString } = useLocaleDateString();

  const userEntry = signal.installed?.users?.find(
    (installedUser) => installedUser.userId === currentUserId
  );

  const isActive = userEntry?.status === SignalInstalledStatus.ACTIVE;

  const appliedById = userEntry?.appliedBy;
  const isApplied = !!appliedById;
  const appliedByName = appliedById
    ? employeeMap.get(appliedById)?.fullName ?? appliedById
    : undefined;

  const activatedCount =
    signal.installed?.users?.filter(
      (installedUser) => installedUser.status === SignalInstalledStatus.ACTIVE
    ).length ?? 0;

  const toggle = (
    <SignalToggle
      isActive={!!isActive}
      onToggle={() => onToggleActive(signal, !!isActive)}
      disabled={isTogglingActive || isApplied}
    />
  );

  return (
    <div
      className={`relative flex cursor-pointer flex-col overflow-hidden rounded-[14px] border bg-[#fffffe] shadow-[0_2px_10px_rgba(0,0,0,0.05)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(0,0,0,0.1)] ${
        isActive
          ? 'border-[#ece8e1]'
          : 'border-[#ece8e1] opacity-80 hover:opacity-90'
      }`}
      onClick={() => onSignalClick(signal)}
    >
      <div className="flex flex-1 flex-col p-5">
        {/* Header: severity badge + activated count + toggle */}
        <div className="mb-1.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SeverityBadge severity={signal.severity} />
            {signal.category === SignalCategory.COMMITMENT ? (
              <Badge
                variant="outline"
                className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]"
              >
                {t('categoryCommitment')}
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="bg-sky-50 text-sky-700 border-sky-200 text-[10px]"
              >
                {t('categoryPlaybook')}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            {activatedCount > 0 && (
              <span className="flex items-center gap-1 text-[11px] text-[#8c8078]">
                <Users className="h-3.5 w-3.5" />
                {activatedCount}
              </span>
            )}
            {isApplied && appliedByName ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="opacity-45 cursor-not-allowed">
                      {toggle}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('appliedByTooltip')(appliedByName)}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              toggle
            )}
          </div>
        </div>

        {/* Signal text */}
        <div className="flex-1 pt-0.5 pb-2">
          <p className="line-clamp-3 h-[72px] text-[14.5px] italic leading-[1.65] text-[#2c2520]">
            <MentionText
              title={signal.title}
              resolvedRelevantParties={signal.resolvedRelevantParties}
            />
          </p>
        </div>

        {/* Related issues count */}
        {signal.relatedIssuesCount > 0 && (
          <div className="flex items-center gap-1.5 pb-2 text-xs text-[#8c8078]">
            <Zap className="h-3.5 w-3.5" />
            <span>{t('relatedIssues')(signal.relatedIssuesCount)}</span>
          </div>
        )}

        {/* Footer: avatar + owner name + date */}
        <div className="mt-2 border-t border-[#f0ece6] pt-3">
          <div className="flex items-center gap-2">
            <SignalAuthorDisplay owner={owner} size="sm" />
            <span className="ms-auto text-[11px] text-[#a39a91]">
              {signal.metadata?.createdAt
                ? toLocaleDateString(signal.metadata.createdAt.toString(), {
                    month: 'short',
                    day: 'numeric',
                  })
                : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Sweep bar — active cards get default color, applied get purple */}
      {isActive && (
        <SweepBar
          signalId={signal._id}
          variant={isApplied ? 'applied' : 'default'}
        />
      )}
    </div>
  );
};
